import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface Tutorial {
  id: string;
  title: string;
  slug: string;
  description: string;
  coverImage: string | null;
  category: string;
  difficulty: string;
  estimatedTime: number | null;
  videoUrl: string | null;
  viewCount: number;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  iniciando: "Iniciando",
  modulos: "Módulos",
  integracoes: "Integrações",
  relatorios: "Relatórios",
  admin: "Administração",
  avancado: "Avançado",
};

const difficultyLabels: Record<string, string> = {
  beginner: "Iniciante",
  intermediate: "Intermediário",
  advanced: "Avançado",
};

const difficultyColors: Record<string, string> = {
  beginner: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  intermediate: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  advanced: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

export default function TutorialsPage() {
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/api/tutorials")
      .then((json) => {
        if (json.success) {
          setTutorials(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(tutorials.map((t) => t.category))];

  const filteredTutorials = tutorials.filter((t) => {
    if (selectedCategory && t.category !== selectedCategory) return false;
    if (selectedDifficulty && t.difficulty !== selectedDifficulty) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title="Tutoriais"
        description="Aprenda a usar o ZWeb com nossos tutoriais passo a passo. Guias para iniciantes e usuários avançados."
        keywords={["tutoriais", "zweb", "erp", "guias", "aprender"]}
        canonicalUrl="/tutoriais"
      />

      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/blog" className="text-sm font-medium hover:text-accent-600">Blog</a>
            <a href="/tutoriais" className="text-sm font-medium text-accent-600">Tutoriais</a>
            <a href="/casos-de-sucesso" className="text-sm font-medium hover:text-accent-600">Cases</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Tutoriais</h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Aprenda a usar o ZWeb com nossos guias passo a passo.
          </p>
        </div>

        {/* Filtros */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? "bg-accent-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              Todos
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-accent-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {categoryLabels[category] || category}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSelectedDifficulty(null)}
              className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                selectedDifficulty === null
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              Todos os níveis
            </button>
            {["beginner", "intermediate", "advanced"].map((diff) => (
              <button
                key={diff}
                onClick={() => setSelectedDifficulty(diff)}
                className={`rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                  selectedDifficulty === diff
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {difficultyLabels[diff]}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de tutoriais */}
        {loading ? (
          <div className="mt-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
          </div>
        ) : filteredTutorials.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum tutorial encontrado com os filtros selecionados.
            </p>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredTutorials.map((tutorial) => (
              <a
                key={tutorial.id}
                href={`/tutoriais/${tutorial.slug}`}
                className="group rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
              >
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${difficultyColors[tutorial.difficulty]}`}>
                    {difficultyLabels[tutorial.difficulty]}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    {categoryLabels[tutorial.category] || tutorial.category}
                  </span>
                </div>

                <h2 className="mt-4 text-lg font-semibold group-hover:text-accent-600">
                  {tutorial.title}
                </h2>

                {tutorial.description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {tutorial.description}
                  </p>
                )}

                <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                  {tutorial.estimatedTime && (
                    <span className="flex items-center gap-1">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {tutorial.estimatedTime} min
                    </span>
                  )}
                  {tutorial.videoUrl && (
                    <span className="flex items-center gap-1">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Vídeo
                    </span>
                  )}
                  <span>{tutorial.viewCount} visualizações</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="container text-center text-sm text-gray-600 dark:text-gray-400">
          © 2026 ZWeb. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
